import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Table',
    width: '28%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Type',
    width: '10%',
    filter: {
      type: 'aggregation',
      aggregationName: 'asset_type_aggregation',
      remoteSearchFields: ['asset_type'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'asset_type',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.database_name',
    label: 'Database',
    width: '18%',
    filter: {
      type: 'aggregation',
      aggregationName: 'database_aggregation',
      remoteSearchFields: ['database_name'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'database_name',
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    width: '11%',
    filter: {
      type: 'aggregation',
      aggregationName: 'env_aggregation',
      remoteSearchFields: ['env'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'env',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.schema_name',
    label: 'Schema',
    width: '13%',
    filter: {
      type: 'text',
      remoteSearchFields: ['schema_name'],
    },
    sortable: true,
    remoteSortField: 'schema_name',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.pi_present',
    label: 'PI Present',
    width: '10%',
    filter: {
      type: 'aggregation',
      aggregationName: 'pi_present_aggregation',
      remoteSearchFields: ['pi_present'],
      filterSelectProps: {
        isClearable: true,
      },
    },
  },
  { name: 'lineage', label: 'Lineage', width: '10%' },
];

const attributesMap: Attribute[] = [
  {
    name: 'sourceSpecificMetadata.Snowflake.unused_table_status',
    label: 'Access Recency',
    showInColumn: 0,
    type: 'string',
    description: 'Access Recency',
    isSearchable: true,
  },
  {
    name: 'technicalMetadata.created_time',
    label: 'Created',
    showInColumn: 0,
    description: 'Created date',
    type: 'date',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered',
    label: 'Last Altered',
    showInColumn: 1,
    description: 'Last altered date',
    type: 'date',
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    showInColumn: 0,
    description: 'Environment',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.table_size_bytes',
    label: 'Table Size',
    showInColumn: 1,
    suffix: ' gigabytes',
    description: 'Table Size in gigabytes',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.airflowDagUrl',
    label: 'Airflow DAG URL',
    showInColumn: 1,
    type: 'link',
    description: 'Airflow DAG URL',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.airflowTaskID',
    label: 'Airflow Task ID',
    showInColumn: 1,
    type: 'string',
    description: 'Airflow Task ID',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.airflowDagID',
    label: 'Airflow DAG ID',
    showInColumn: 1,
    type: 'string',
    description: 'Airflow DAG ID',
  },
  {
    name: 'technicalMetadata.asset_age',
    label: 'Table Age',
    showInColumn: 0,
    suffix: ' days',
    description: 'Table Age',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.row_count',
    label: 'Rows',
    showInColumn: 1,
    description: 'Number of rows in millions',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_accessed',
    label: 'Last Access Timestamp',
    showInColumn: 1,
    description: 'Timestamp of last access to the table',
    type: 'date',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_accessed_age',
    label: 'Last Accessed Age',
    showInColumn: 1,
    suffix: ' days',
    description: 'Last accessed age in days',
  },
  {
    name: 'technicalMetadata.usageMetadata.user_count',
    label: 'Unique Users Count',
    showInColumn: 1,
    suffix: ' within the last 13 months',
    description: 'Number of unique users who accessed the table',
  },
  {
    name: 'technicalMetadata.operationalMetadata.vcs',
    label: 'Version Control',
    type: 'link',
    showInColumn: 0,
    description: 'Version Control URL',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.schema_name',
    label: 'Schema',
    showInColumn: 1,
    description: 'Asset schema name',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.documentation',
    label: 'Documentation',
    showInColumn: 0,
    description: 'Documentation URL',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.is_cloned',
    label: 'Is Cloned',
    type: 'boolean',
    showInColumn: 1,
    description: 'Define if the asset has been cloned',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.clustering_key',
    label: 'Clustering Key',
    showInColumn: 1,
    description: 'Clustering Key',
  },
  {
    name: 'technicalMetadata.created_by',
    label: 'Created by',
    showInColumn: 0,
    description: 'Created by',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Type',
    showInColumn: 0,
    description: 'Asset type',
    isSearchable: true,
  },
  {
    name: 'technicalMetadata.asset_status',
    label: 'Status',
    showInColumn: 0,
    description: 'Asset status',
  },
  {
    name: 'businessMetadata.administrativeMetadata.owners',
    label: 'Owners',
    type: 'owners',
    showInColumn: 2,
    description: 'Owners',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.applicable_policies',
    label: 'Applicable Policies',
    type: 'link',
    showInColumn: 2,
    description: 'Applicable Policies',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    showInColumn: 2,
    description: 'Description',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.business_description',
    label: 'Business Description',
    showInColumn: 2,
    description: 'Business Description',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.gen_ai_description',
    label: 'AI Generated Description',
    showInColumn: 2,
    description: 'AI Generated Description',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.data_classification',
    label: 'Data Classification',
    showInColumn: 2,
    description: 'Data Classification',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.data_sensitivity',
    label: 'Data Sensitivity',
    showInColumn: 2,
    description: 'Data Sensitivity',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.business_segment',
    label: 'Business Segment',
    type: 'array',
    showInColumn: 2,
    description: 'Business Segment',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.business_sensitivity',
    label: 'Business Sensitivity',
    showInColumn: 2,
    description: 'Business Sensitivity',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.is_aggregated',
    label: 'Is Aggregated',
    showInColumn: 2,
    description: 'Is Aggregated',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.child_data',
    label: 'Child Data',
    showInColumn: 2,
    description: 'Child Data',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.region',
    label: 'Region',
    showInColumn: 2,
    description: 'Region',
  },
  {
    name: 'technicalMetadata.securityAndComplianceMetadata.sub_region',
    label: 'Sub Region',
    type: 'array',
    showInColumn: 2,
    description: 'Sub Region',
  },
  {
    name: 'technicalMetadata.operationalMetadata.act_monthly_storage_cost',
    label: 'Storage Monthly Cost',
    type: 'string',
    prefix: '$',
    showInColumn: 1,
    description: 'Storage Monthly Cost',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.database_name',
    label: 'Database',
    showInColumn: 1,
    description: 'Database name',
    isSearchable: true,
  },
  {
    name: 'technicalMetadata.usageMetadata.top_users',
    label: 'Top Users',
    showInColumn: 1,
    type: 'array',
    description: 'Top users who accessed the table',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.is_shared',
    label: 'Is Shared',
    showInColumn: 2,
    type: 'boolean',
    description: 'Indicates if the table is shared',
  },
  {
    name: 'sourceSpecificMetadata.Snowflake.source_account',
    label: 'Source Account',
    showInColumn: 2,
    description: 'Source account for the table',
  },
];

const customQueryForListPage = `
  Snowflake {
    database_name
    schema_name
    unused_table_status
    pi_present
  }
`;

const customQueryForDetailsPage = `
  Snowflake {
    airflowDagID
    airflowDagUrl
    airflowTaskID
    clustering_key
    columns {
      businessDescription
      columnName
      dataType
      dataTypeDetails
      defaultValue
      description
      gen_ai_description
      nullable
      pi_present
      pi_types
      tags {
        name
        value
      }
    }
    custom_fields {
      name
      value
    }
    database_name
    deleted
    documentation
    fail_safe_bytes
    is_cloned
    is_shared
    retained_for_clone_bytes
    row_count
    schema_name
    source_account
    table_id
    table_size_bytes
    time_travel_bytes
    unused_table_status
  }
`;

const searchableFields = [...getSearchableFields(attributesMap)];

export const SnowflakeConfig: DataSource = {
  id: '1',
  name: 'Snowflake',
  showColumns: true,
  showPartitionColumns: false,
  showLineage: true,
  showLineageTable: true,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineageColumn: true,
  showLineagePipeline: false,
  showLineageDashboard: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  searchableFields,
};
